import React, { useEffect, useState, useRef } from 'react';
import styled, { css } from 'styled-components';

import type { ScopedTalknClient } from '@api-client/TalknAPI';
import Favicon from '../atoms/Favicon';
import LiveCnt from '../atoms/LiveCnt';
import OgpImage from '../atoms/OgpImage';
import SnsLinks from '../atoms/SnsLinks';
import { P, H5 } from '../flexes';
import styles from '../../styles';

export type ServerMetasType = {
  'og:image': string;
  'og:description': string;
  'twitter:site': string;
  'fb:page_id': string;
  'al:ios:app_store_id': string;
  'al:android:package': string;
};

export type ArticleType = {
  ch: string;
  title: string;
  favicon: string;
  updateTime: string;
  liveCnt: number;
  serverMetas: ServerMetasType;
  linkUrl: string;
};

export type Props = {
  talknAPI: ScopedTalknClient;
  article: ArticleType;
  index: number;
  focusIndex: undefined | number;
  setFocusIndex: React.Dispatch<React.SetStateAction<undefined | number>>;
};

const ArticleCard: React.FC<Props> = ({ talknAPI, article, index, focusIndex, setFocusIndex }) => {
  const { serverMetas } = article;
  const [marqueeOn, setMarqueeOn] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<number>(0);
  const isFocus = index === focusIndex;

  const handleOnMouseOver = () => {
    setFocusIndex(index);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => {
      setFocusIndex(undefined);
    }, 7000);
  };

  const handleOnMouseMove = () => {
    setFocusIndex(index);
  };

  const handleOnMouseLeave = () => {
    clearTimeout(timeoutRef.current);
    setFocusIndex(undefined);
  };

  // did mount: マーキー判定 + card_view（インプレッション）送信
  useEffect(() => {
    if (headerRef.current) {
      const headerElm = headerRef.current;
      const titleElm = headerElm.children[1] as HTMLElement;
      if (titleElm) {
        setMarqueeOn(titleElm.clientWidth < titleElm.scrollWidth);
      }
    }
    // カード表示イベントを送信
    talknAPI.trackCardView(article.ch);
  }, []);

  // URLを正規化（プロトコルがない場合はhttps://を追加）
  const normalizedUrl = (() => {
    const url = article.linkUrl;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `https://${url.replace(/^\/+/, '')}`;
  })();

  const handleCardClick = () => {
    // card_click（コンバージョン）イベントを送信（fire-and-forget）
    talknAPI.trackCardClick(article.ch, normalizedUrl);
  };

  return (
    <Container>
      <Cover onMouseOver={handleOnMouseOver}>
        <Header $overflowTitle={marqueeOn}>
          <Favicon src={article.favicon} className={'Favicon'} />
          <H5 id={'Article'} className={'Title'}>
            {article.title}
          </H5>
          <LiveCnt className={'LiveCnt'}>{article.liveCnt}</LiveCnt>
        </Header>
        <OgpImage src={serverMetas['og:image']} ch={article.ch} />
      </Cover>
      <DetailLink
        href={normalizedUrl}
        target="_blank"
        rel="noopener noreferrer"
        $isFocus={isFocus}
        $marqueeDuration={marqueeOn ? article.title.length / 10 : 0}
        onMouseOver={handleOnMouseOver}
        onMouseMove={handleOnMouseMove}
        onClick={handleCardClick}>
        <Header ref={headerRef} $overflowTitle={marqueeOn}>
          <Favicon src={article.favicon} className={'Favicon'} />
          <H5 id={'Article'} className={'Title'}>
            {article.title}
          </H5>
          <LiveCnt className={'LiveCnt'}>{article.liveCnt}</LiveCnt>
        </Header>
        <OgpImage src={serverMetas['og:image']} ch={article.ch} />
        <Description>
          <P>{serverMetas['og:description']}</P>
          <SnsLinks serverMetas={serverMetas} />
        </Description>
      </DetailLink>
    </Container>
  );
};

export default ArticleCard;

const Container = styled.div`
  width: var(--talkn-card-width, ${styles.articleWidth}px);
  height: var(--talkn-card-height, ${styles.articleCloseHeight}px);
`;

const Cover = styled.div`
  box-sizing: border-box;
  overflow: hidden;
  width: inherit;
  height: var(--talkn-card-height, ${styles.articleCloseHeight}px);
  background: var(--talkn-card-bg, #fff);
  box-shadow: ${styles.shadowHorizonBright};
  border-radius: var(--talkn-border-radius, 10px);
`;

type DetailPropsType = {
  $isFocus: boolean;
  $marqueeDuration: number;
};

const marqueeCss = css<DetailPropsType>`
  @keyframes animation-marquee {
    0% {
      transform: translate(0%);
    }
    100% {
      transform: translate(-100%);
    }
  }
  @-webkit-keyframes animation-marquee {
    0% {
      transform: translate(0%);
    }
    100% {
      transform: translate(-100%);
    }
  }
  animation-name: animation-marquee;
  animation-duration: ${(props) => props.$marqueeDuration}s;
  animation-timing-function: linear;
  animation-delay: 0s;
  animation-iteration-count: infinite;
  animation-direction: normal;
`;

const DetailLink = styled.a<DetailPropsType>`
  display: block;
  z-index: 20;
  position: absolute;
  top: var(--talkn-card-gap, ${styles.basePadding}px);
  left: var(--talkn-card-gap, ${styles.basePadding}px);
  text-decoration: none;
  color: inherit;
  opacity: ${(props) => (props.$isFocus ? 1 : 0)};
  pointer-events: ${(props) => (props.$isFocus ? 'auto' : 'none')};
  width: inherit;
  height: ${styles.articleOpenHeight};
  background: var(--talkn-card-bg, #fff);
  box-shadow: 0px 0px ${styles.shadowSize / 2}px 0px var(--talkn-shadow-color, ${styles.articleShadowColor});
  border-radius: var(--talkn-border-radius, 10px);
  transition-property: opacity, transform, height;
  transition-duration: ${(props) => (props.$isFocus ? '300ms, 300ms, 0ms' : '0ms, 0ms, 0ms')};
  transform: ${(props) => (props.$isFocus ? `scale(var(--talkn-hover-scale, ${styles.articleOpenScale})) translate(0, 10px)` : 'scale(1) translate(0, 0)')};
  cursor: pointer;
  .Title {
    display: flex;
    ${(props) => (props.$marqueeDuration ? marqueeCss : '')};
    text-decoration: none;
  }
  .LiveCnt {
    text-decoration: none;
  }
`;

type HeaderPropsType = {
  $overflowTitle: boolean;
};

const Header = styled.header<HeaderPropsType>`
  overflow: hidden;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: flex-start;
  width: inherit;
  height: var(--talkn-header-height, 60px);
  white-space: nowrap;
  border-radius: var(--talkn-border-radius, 10px) var(--talkn-border-radius, 10px) 0 0;
  border-top: 1px solid var(--talkn-border-color, #eee);
  border-right: 1px solid var(--talkn-border-color, #eee);
  border-left: 1px solid var(--talkn-border-color, #eee);
  border-bottom: 0;
  .Favicon {
    flex: 1 1 50px;
  }
  .Title {
    font-weight: var(--talkn-title-font-weight, 300);
    flex: 1 1 200px;
    max-width: 200px;
    text-align: center;
    color: var(--talkn-font-color, ${styles.fontColor});
    text-decoration: none;
  }
  .LiveCnt {
    flex: 1 1 50px;
    text-decoration: none;
  }
`;

const Description = styled.div`
  overflow: hidden;
  margin: 8px 15px 24px;
  p {
    line-height: 26px;
    letter-spacing: 1.3px;
    color: var(--talkn-font-color, ${styles.fontColor});
    font-size: var(--talkn-desc-font-size, 90%);
    font-weight: 200;
    text-decoration: none;
  }
`;
